/**
 * Dashboard grid layout helpers (issue #423/#449/#450).
 *
 * Pure functions for building the default dashboard layout, converting
 * between the persisted `DashboardLayout` shape (backend contract, #449) and
 * `react-grid-layout`'s `LayoutItem[]` shape, and mutating the layout when
 * the user adds or removes a widget in Configure edit mode.
 */

import type { Layout, LayoutItem } from 'react-grid-layout';
import type {
  AvailableWidget,
  DashboardLayout,
  DashboardWidget,
  DashboardWidgetFilter,
  DashboardWidgetPosition,
} from '../types/preferences';

/** The dashboard grid is 12 columns wide (homepage-dashboard-spec.md "UI/UX Guidelines"). */
export const GRID_COLUMNS = 12;

/** Default row height used across the grid (matches the default widget height below). */
export const DEFAULT_ROW_HEIGHT = 4;

/**
 * Maps a widget's catalog `defaultSize` to a grid column width.
 * Matches homepage-dashboard-spec.md "UI/UX Guidelines": Small (4 col),
 * Medium (6 col), Large (8 col).
 */
export function sizeToGridWidth(size: 'small' | 'medium' | 'large'): number {
  switch (size) {
    case 'small':
      return 4;
    case 'large':
      return 8;
    case 'medium':
    default:
      return 6;
  }
}

/**
 * Generates a unique `instanceId` for a newly-added widget placement (issue
 * #1260). Suffixed with a random UUID rather than left as the bare
 * `widgetId` so it can never collide with the deterministic
 * `instanceId = widgetId` that `backfillInstanceIds` assigns to a pre-#1260
 * legacy entry of the same widget type.
 */
function generateInstanceId(widgetId: string): string {
  return `${widgetId}-${crypto.randomUUID()}`;
}

/**
 * Backfills `instanceId` (issue #1260's identity key, see
 * [ADR 1260](../../../documents/decisions/1260-multi-instance-widgets-instanceid-identity.md))
 * on any widget missing it. Legacy `dashboardLayout` blobs saved before #1260
 * have no `instanceId`; deriving it from `widgetId` is safe and collision-free
 * because no pre-#1260 layout could ever contain two entries sharing a
 * `widgetId` — that uniqueness was exactly the constraint #1260 relaxes, so
 * this derived id can never clash with another entry already in the same
 * layout. Called at every point `dashboardLayout` enters the store (server
 * hydration) so downstream code can always rely on `instanceId` being present.
 */
export function backfillInstanceIds(layout: DashboardLayout): DashboardLayout {
  if (layout.widgets.every((w) => w.instanceId)) {
    return layout;
  }
  return {
    ...layout,
    widgets: layout.widgets.map((w) => (w.instanceId ? w : { ...w, instanceId: w.widgetId })),
  };
}

/**
 * Builds the default dashboard layout from the widget catalog: widgets are
 * placed in catalog order, two per row (a `small`+`small` or `medium`+`medium`
 * pair fits within the 12-column grid; a `large` widget takes its own row).
 * Each default instance's `instanceId` is its `widgetId` — matching the
 * deterministic id `backfillInstanceIds` assigns to legacy data, so a
 * brand-new default layout and a backfilled legacy one are indistinguishable.
 */
export function buildDefaultLayout(catalog: readonly AvailableWidget[]): DashboardLayout {
  const widgets: DashboardWidget[] = [];
  let x = 0;
  let y = 0;

  for (const entry of catalog) {
    const w = sizeToGridWidth(entry.defaultSize);
    if (x + w > GRID_COLUMNS) {
      x = 0;
      y += DEFAULT_ROW_HEIGHT;
    }
    widgets.push({
      instanceId: entry.id,
      widgetId: entry.id,
      position: { x, y, w, h: DEFAULT_ROW_HEIGHT },
    });
    x += w;
    if (x >= GRID_COLUMNS) {
      x = 0;
      y += DEFAULT_ROW_HEIGHT;
    }
  }

  return { widgets, layoutMode: 'grid' };
}

/**
 * Returns the row (`y`) immediately below the lowest occupied cell in the
 * layout — used to place a newly-added widget below all existing ones.
 */
function nextAvailableRow(widgets: readonly DashboardWidget[]): number {
  return widgets.reduce((max, w) => Math.max(max, w.position.y + w.position.h), 0);
}

/**
 * Appends a new instance of a catalog widget to the layout, placed at its
 * `defaultSize` in a new row below all existing widgets, with a freshly
 * generated `instanceId`. Issue #1260/[ADR 1260](../../../documents/decisions/1260-multi-instance-widgets-instanceid-identity.md)
 * made every widget multi-instance-eligible, so — unlike the pre-#1260
 * behavior — this always appends; it is never a no-op when `widget.id` is
 * already present in the layout.
 */
export function addWidgetToLayout(
  layout: DashboardLayout,
  widget: AvailableWidget
): DashboardLayout {
  const y = nextAvailableRow(layout.widgets);
  const w = sizeToGridWidth(widget.defaultSize);
  return {
    ...layout,
    widgets: [
      ...layout.widgets,
      {
        instanceId: generateInstanceId(widget.id),
        widgetId: widget.id,
        position: { x: 0, y, w, h: DEFAULT_ROW_HEIGHT },
      },
    ],
  };
}

/**
 * Removes a single widget instance from the layout by `instanceId` (issue
 * #1260 — not `widgetId`, since multiple instances of the same widget type
 * may coexist). No-op if not present.
 */
export function removeWidgetFromLayout(
  layout: DashboardLayout,
  instanceId: string
): DashboardLayout {
  return {
    ...layout,
    widgets: layout.widgets.filter((w) => w.instanceId !== instanceId),
  };
}

/**
 * Converts the persisted `DashboardLayout` into `react-grid-layout`'s
 * `LayoutItem[]` shape (`{ i, x, y, w, h }`), keyed by `instanceId` (issue
 * #1260 — `widgetId` is no longer unique per layout, so it can no longer
 * serve as the grid item key).
 */
export function toGridLayout(layout: DashboardLayout): Layout {
  return layout.widgets.map((w) => ({
    i: w.instanceId,
    x: w.position.x,
    y: w.position.y,
    w: w.position.w,
    h: w.position.h,
  }));
}

/**
 * Converts a `react-grid-layout` `LayoutItem[]` (from `onLayoutChange`) back
 * into the persisted `DashboardLayout` shape, preserving each widget's
 * `widgetId`, `config`, `title`, and `filters` from the previous layout,
 * matched by `instanceId` (issue #1260). `gridLayout` is always derived from
 * `previousLayout` via `toGridLayout`, so every item's `i` is expected to
 * match an entry in `previousLayout`; any item without a match (which should
 * not happen under that invariant) is dropped rather than producing a
 * malformed widget with no `widgetId`.
 */
export function fromGridLayout(
  gridLayout: readonly LayoutItem[],
  previousLayout: DashboardLayout
): DashboardLayout {
  const byId = new Map(previousLayout.widgets.map((w) => [w.instanceId, w]));
  const position = (item: LayoutItem): DashboardWidgetPosition => ({
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
  });

  return {
    ...previousLayout,
    widgets: gridLayout
      .filter((item) => byId.has(item.i))
      .map((item) => {
        const previous = byId.get(item.i);
        // Non-null: filtered by `byId.has(item.i)` above.
        const { widgetId, config, title, filters } = previous as DashboardWidget;
        return {
          instanceId: item.i,
          widgetId,
          position: position(item),
          config,
          title,
          filters,
        };
      }),
  };
}

/**
 * Sets (replaces) a single widget instance's `config` in the layout (issue
 * #491's scope toggle, and any future per-widget setting). Matched by
 * `instanceId` (issue #1260). No-op if `instanceId` is not present in the
 * layout.
 */
export function updateWidgetConfig(
  layout: DashboardLayout,
  instanceId: string,
  config: Record<string, unknown>
): DashboardLayout {
  return {
    ...layout,
    widgets: layout.widgets.map((w) => (w.instanceId === instanceId ? { ...w, config } : w)),
  };
}

/**
 * Sets (replaces) a single widget instance's custom display `title` (issue
 * #489). Matched by `instanceId` (issue #1260). Passing `undefined` clears
 * the override, falling back to the catalog title.
 */
export function updateWidgetTitle(
  layout: DashboardLayout,
  instanceId: string,
  title: string | undefined
): DashboardLayout {
  return {
    ...layout,
    widgets: layout.widgets.map((w) => (w.instanceId === instanceId ? { ...w, title } : w)),
  };
}

/**
 * Sets (replaces) a single widget instance's tag/category `filters` (issue
 * #489). Matched by `instanceId` (issue #1260). Passing `undefined` clears
 * the filter, showing the widget's unfiltered data.
 */
export function updateWidgetFilters(
  layout: DashboardLayout,
  instanceId: string,
  filters: DashboardWidgetFilter | undefined
): DashboardLayout {
  return {
    ...layout,
    widgets: layout.widgets.map((w) => (w.instanceId === instanceId ? { ...w, filters } : w)),
  };
}

/**
 * Returns the catalog options offered by the "Add widget" picker
 * (UJ-DASH-15/18). Per issue #1260/[ADR 1260](../../../documents/decisions/1260-multi-instance-widgets-instanceid-identity.md),
 * every catalog widget is multi-instance-eligible, so — unlike the pre-#1260
 * behavior — this no longer excludes widgets already present in `layout`;
 * it exists as a stable seam future per-widget "singleton" exceptions could
 * hook into without touching call sites, per the ADR's Consequences.
 */
export function getAddableWidgets(
  catalog: readonly AvailableWidget[],
  _layout: DashboardLayout
): AvailableWidget[] {
  return [...catalog];
}
